import { Outlet, Link, useLocation, useNavigate } from "react-router";
import { Users, Settings, Sliders, Building2, X, Home, Radio, BookOpen, type LucideIcon } from "lucide-react";
import { useRootLoader } from "@/hooks/useRootLoader";
import {
  ROUTE_ADMIN,
  ROUTE_ADMIN_USERS,
  ROUTE_ADMIN_TENANT_SETTINGS,
  ROUTE_ADMIN_PRESETS,
  ROUTE_ADMIN_COMPANIES,
  ROUTE_ADMIN_DICTIONARIES,
  ROUTE_ADMIN_REALTIME_CONFIGS,
  ROUTE_403,
  ROUTE_TOP,
} from "@/utils/const";
import { useEffect, useState } from "react";
import type { WhoamiMe } from "@shared-domain/auth-service/contract";

type MenuItem = {
  path: string;
  icon: LucideIcon;
  label: string;
  badge: "admin" | "sysadmin";
  sysadminOnly: boolean;
};

const allMenuItems: MenuItem[] = [
  { path: ROUTE_ADMIN_COMPANIES, icon: Building2, label: "企業マスタ管理", badge: "admin", sysadminOnly: false },
  { path: ROUTE_ADMIN_DICTIONARIES, icon: BookOpen, label: "テナント辞書", badge: "admin", sysadminOnly: false },
  { path: ROUTE_ADMIN_TENANT_SETTINGS, icon: Settings, label: "テナント設定", badge: "sysadmin", sysadminOnly: true },
  { path: ROUTE_ADMIN_USERS, icon: Users, label: "ユーザー管理", badge: "sysadmin", sysadminOnly: true },
  { path: ROUTE_ADMIN_PRESETS, icon: Sliders, label: "プリセット管理", badge: "sysadmin", sysadminOnly: true },
  { path: ROUTE_ADMIN_REALTIME_CONFIGS, icon: Radio, label: "リアルタイムAPI設定", badge: "sysadmin", sysadminOnly: true },
];

/**
 * フロントでのadmin権限チェック（改ざんは許容）
 */
// eslint-disable-next-line react-refresh/only-export-components
export const checkRoleIsAdmin = (auth: WhoamiMe): boolean => {
  if (!auth || !auth.currentTenantId || !auth.info) {
    return false;
  }
  const tenantId = auth.currentTenantId;
  const productId = auth.info.productId;
  const membership = auth.memberships.find(
    (x) => x.tenantId === tenantId && x.products[0].id === productId,
  );
  if (!membership) {
    return false;
  }
  return membership.role === "admin";
};

/**
 * フロントでのsysadmin権限チェック（admin + specialFlags["sysadmin"]）
 */
// eslint-disable-next-line react-refresh/only-export-components
export const checkRoleIsSysadmin = (auth: WhoamiMe): boolean => {
  if (!checkRoleIsAdmin(auth)) return false;
  const tenantId = auth.currentTenantId;
  const productId = auth.info!.productId;
  const membership = auth.memberships.find(
    (x) => x.tenantId === tenantId && x.products[0].id === productId,
  );
  return membership?.specialFlags?.includes("sysadmin") ?? false;
};

function PermissionBadge({ badge }: { badge: "admin" | "sysadmin" }) {
  if (badge === "sysadmin") {
    return (
      <span className="text-[10px] leading-none px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
        sysadmin
      </span>
    );
  }
  return (
    <span className="text-[10px] leading-none px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
      admin
    </span>
  );
}

export default function AdminLayout() {
  const { auth } = useRootLoader();
  const location = useLocation();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);

  const isAdmin = checkRoleIsAdmin(auth);
  const isSysadmin = checkRoleIsSysadmin(auth);

  useEffect(() => {
    if (!isAdmin) {
      navigate(ROUTE_403, { replace: true });
    }
  }, [isAdmin, navigate]);

  if (!isAdmin) {
    return null;
  }

  const menuItems = allMenuItems.filter(
    (item) => !item.sysadminOnly || isSysadmin,
  );

  return (
    <>
      {/* モバイル・タブレット表示 */}
      <div className="md:hidden min-h-screen bg-neutral-50">
        <main>
          <Outlet context={{ setShowMenu }} />
        </main>

        {showMenu && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setShowMenu(false)}
          >
            <div
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-safe-bottom"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-neutral-900">メニュー</h2>
                <button
                  onClick={() => setShowMenu(false)}
                  className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
                >
                  <X size={24} className="text-neutral-600" />
                </button>
              </div>

              <div className="space-y-2">
                <Link
                  to={ROUTE_ADMIN}
                  onClick={() => setShowMenu(false)}
                  className={`flex items-center space-x-3 p-4 rounded-lg transition-colors ${
                    location.pathname === ROUTE_ADMIN
                      ? "bg-neutral-100"
                      : "hover:bg-neutral-100"
                  }`}
                >
                  <Home size={24} />
                  <span className="font-medium">トップ</span>
                </Link>
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname.startsWith(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setShowMenu(false)}
                      className={`flex items-center space-x-3 p-4 rounded-lg transition-colors ${
                        isActive
                          ? "bg-neutral-100"
                          : "hover:bg-neutral-100"
                      }`}
                    >
                      <Icon size={24} />
                      <span className="font-medium flex-1">{item.label}</span>
                      <PermissionBadge badge={item.badge} />
                    </Link>
                  );
                })}
              </div>

              <div className="mt-4 pt-4 border-t border-neutral-200">
                <Link
                  to={ROUTE_TOP}
                  onClick={() => setShowMenu(false)}
                  className="w-full flex items-center space-x-3 p-4 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  <Home size={24} className="text-neutral-700" />
                  <span className="text-neutral-900 font-medium">
                    ダッシュボードへ戻る
                  </span>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* デスクトップ表示 */}
      <div className="hidden md:flex h-screen bg-gray-100">
        <aside className="w-80 bg-white shadow-md flex flex-col">
          <div className="p-4 border-b">
            <h2 className="text-xl font-bold text-gray-800">管理画面</h2>
          </div>
          <nav className="p-4 flex-1">
            <ul className="space-y-2">
              <li>
                <Link
                  to={ROUTE_ADMIN}
                  className={`flex items-center gap-3 px-4 py-2 rounded-md transition-colors ${
                    location.pathname === ROUTE_ADMIN
                      ? "bg-gray-100 text-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Home size={20} />
                  <span>トップ</span>
                </Link>
              </li>
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-2 rounded-md transition-colors ${
                        isActive
                          ? "bg-gray-100 text-gray-700"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <Icon size={20} />
                      <span className="flex-1">{item.label}</span>
                      <PermissionBadge badge={item.badge} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div className="p-4 border-t bg-white">
            <Link
              to={ROUTE_TOP}
              className="flex items-center gap-3 px-4 py-4 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Home size={20} />
              <span>ダッシュボードへ戻る</span>
            </Link>
          </div>
        </aside>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </>
  );
}
